import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { toast } from "@shared/hooks/use-toast";
import { RotateCcw, FileSpreadsheet, Eye } from "lucide-react";
import { PAGE_SIZE } from "@shared/utils/constants";
import { SyncActivityFilter } from "./SyncActivityFilter";
import { useGetSyncLogs } from "@features/ix/erpIntegration/hooks";
import { LogDetailDialog } from "@features/ix/shared/LogDetailDialog";

interface SyncLog {
  id: string | number;
  operation: string;
  timestamp: string;
  flow: string;
  recordId: string;
  status: string;
  latencyMs: number;
}

interface FilterValues {
  page?: number;
  limit?: number;
  search?: string;
  flow?: string;
  status?: string;
  dateRange?: string;
}

export const SyncActivityTab: React.FC = () => {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);
  const [filters, setFilters] = useState<FilterValues>({
    page: 1,
    limit: PAGE_SIZE,
    search: "",
    flow: "",
    status: "",
    dateRange: "",
  });

  const form = useForm({
    defaultValues: {
      flow: "",
      status: "",
      dateRange: "",
    },
  });

  const { data: syncLogsData } = useGetSyncLogs(id || "", {
    page: filters.page,
    limit: filters.limit,
    search_data: filters.search,
    flow: filters.flow,
    status: filters.status,
    period: filters.dateRange,
  });

  const paginatedData = useMemo(() => {
    const results = syncLogsData?.results || [];
    return results.map((item: any) => ({
      id: item.id,
      timestamp: item.timestamp || "N/A",
      flow: item.flow || "N/A",
      operation: item.operation || "N/A",
      recordId: item.recordId || "N/A",
      status: item.status || "N/A",
      latencyMs: item.latencyMs || "N/A",
    }));
  }, [syncLogsData]);

  const columns: TableColumn<SyncLog>[] = [
    {
      header: "TIMESTAMP",
      key: "timestamp",
      render: (_val, row) => (
        <span className="text-gray-500 font-semibold">{row.timestamp}</span>
      ),
    },
    {
      header: "FLOW",
      key: "flow",
      render: (_val, row) => (
        <span className="font-extrabold text-gray-900">{row.flow}</span>
      ),
    },
    {
      header: "OPERATION",
      key: "operation",
      render: (_val, row) => (
        <Badge
          variant="outline"
          className="bg-gray-50 border border-gray-200 text-gray-500 font-mono text-[10px] rounded px-2 py-0.5"
        >
          {row.operation}
        </Badge>
      ),
    },
    {
      header: "RECORD",
      key: "recordId",
      render: (_val, row) => (
        <span className="font-extrabold text-gray-900">{row.recordId}</span>
      ),
    },
    {
      header: "STATUS",
      key: "status",
      render: (_val, row) => {
        const isSuccess = row.status?.toLowerCase() === "success";
        return (
          <Badge
            className={`text-[10px] font-bold py-0.5 px-2.5 rounded-full border inline-flex items-center gap-1.5 ${
              isSuccess
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                : "bg-red-50 text-red-700 border-red-200 hover:bg-red-50"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isSuccess ? "bg-emerald-500" : "bg-red-500"
              }`}
            />
            {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
          </Badge>
        );
      },
    },
    {
      header: "LATENCY",
      key: "latencyMs",
      render: (_val, row) => {
        const isFailed = row.status?.toLowerCase() === "failed";
        return (
          <span
            className={`font-semibold ${
              isFailed ? "text-red-500 font-bold" : "text-gray-500"
            }`}
          >
            {row.latencyMs}ms
          </span>
        );
      },
    },
  ];

  const action: TableAction[] = [
    {
      label: "View",
      icon: <Eye className="w-4 h-4" />,
      onClick: (row) => {
        setSelectedLog(row);
        setIsViewDialogOpen(true);
      },
    },
  ];

  const handleApplyFilters = (data: any) => {
    setFilters((prev) => ({
      ...prev,
      flow: data.flow,
      status: data.status,
      dateRange: data.dateRange,
      page: 1,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: PAGE_SIZE,
      search: "",
      flow: "",
      status: "",
      dateRange: "",
    });
    setPage(1);
    form.reset({
      flow: "",
      status: "",
      dateRange: "",
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
    setPage(1);
  };

  const handleExport = () => {};

  const totalItems = syncLogsData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;

  const pagination = {
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
    endItem: Math.min(page * PAGE_SIZE, totalItems),
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header Action */}
      <div className="flex items-center justify-between mx-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              Sync Log History
            </h3>
            <p className="text-xs text-gray-500">
              {totalItems} entries match your filters
            </p>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="p-2">
        <GenericTable
          data={paginatedData}
          columns={columns}
          rowKey="id"
          onSearch={handleSearch}
          searchPlaceholder="Search by record ID..."
          pagination={pagination}
          onPageChange={handlePageChange}
          onExport={handleExport}
          actions={action}
          actionsType="icons"
          filterPanel={
            <SyncActivityFilter
              form={form}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          }
          getRowClassName={() =>
            "text-xs h-14 border-b last:border-0 hover:bg-indigo-50/10 transition-colors"
          }
        />
      </div>

      <LogDetailDialog
        isOpen={isViewDialogOpen}
        onClose={() => setIsViewDialogOpen(false)}
        logData={selectedLog}
      />
    </div>
  );
};

export default SyncActivityTab;
